import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Table,
} from "reactstrap";
import { useLocation } from "react-router-dom";
import { URL } from "../../api";
import axiosInstance from "../../Request";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaSave, FaTimes, FaTrash } from "react-icons/fa";
import breAxios from "./breAxios";
import config from "../../config";


const FintechUpdatePage = () => {
  const location = useLocation();
  const [stateList, setStateList] = useState([]);
  const fintechId = location.search.split("?")[1];
  const [expandedProductVersions, setExpandedProductVersions] = useState({});
  const [productPolicies, setProductPolicies] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [breConfigs, setBreConfigs] = useState([]);
  const [expandedCheckpoint, setExpandedCheckpoint] = useState(null);
  const [form, setForm] = useState({
    name: "",
    userName: "",
    password: "",
    emailId: "",
    phoneNumber: "",
    brandName: "",
    gstNumber: "",
    cinNumber: "",
    companyPanNumber: "",
    companyAddress: "",
    state: "",
    spocName: "",
    spocPhoneNumber: "",
    spocEmail: "",
    grievanceOfficerName: "",
    grievanceOfficerPhone: "",
    grievanceOfficerEmail: "",
    webhookUrl: "", // ✅ Added this
    isActive: true,
    version: "",
    bureauType: 1,
    foreclosure: {
      daily: { paid_emi: 0, foreclosure_fees: 0, force_foreclosure_fees: 0 },
      weekly: { paid_emi: 0, foreclosure_fees: 0, force_foreclosure_fees: 0 },
      monthly: { paid_emi: 0, foreclosure_fees: 0, force_foreclosure_fees: 0 },
      bullet: { paid_emi: 0, foreclosure_fees: 0, force_foreclosure_fees: 0 },
    },
  });



  const [message, setMessage] = useState("");
  const [lspProducts, setLspProducts] = useState({});
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedVersionDetails, setExpandedVersionDetails] = useState({});
  const [editingPolicyKey, setEditingPolicyKey] = useState(null);
  const [productVersion, setProductVersion] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const handlePolicyEditChange = (field, index, key, value) => {
    const updated = [...editFormData[field]];
    updated[index][key] = value;
    setEditFormData((prev) => ({ ...prev, [field]: updated }));
  };



const BRE_MODES = ["AUTO", "MANUAL", "DISABLED"];
const RULE_MODES = ["AUTO", "MANUAL", "DISABLED"];

 /* =========================
     LOAD ALL CONFIG
  ==========================*/
  useEffect(() => {
    loadBreConfig();
  }, []);

  const loadBreConfig = async () => {
    try {
      const res = await breAxios.get(
        `/fintech/allBreConfig?fintechId=${fintechId}`
      );

      const { checkpointModes, ruleModes } = res.data;

      const rows = Object.keys(checkpointModes).map(cp => ({
        checkpoint: Number(cp),
        mode: checkpointModes[cp],
        isActive: checkpointModes[cp] !== "DISABLED",
        thirdParties: mapRules(ruleModes?.[cp])
      }));

      setBreConfigs(rows);
    } catch (e) {
      toast.error("Failed to load BRE config");
    }
  };

  const mapRules = rulesObj => {
    if (!rulesObj) return [];

    return [
      {
        provider: "RULE_ENGINE",
        mode: "AUTO",
        rules: Object.keys(rulesObj).map(r => ({
          ruleCode: r,
          ruleName: r,
          mode: rulesObj[r]
        }))
      }
    ];
  };

  /* =========================
     HANDLERS
  ==========================*/

  const addCheckpoint = () => {
    setBreConfigs([
      ...breConfigs,
      {
        checkpoint: "",
        mode: "AUTO",
        isActive: true,
        thirdParties: []
      }
    ]);
  };

  const removeCheckpoint = idx => {
    const copy = [...breConfigs];
    copy.splice(idx, 1);
    setBreConfigs(copy);
  };

  const updateCheckpoint = (idx, field, value) => {
    const copy = [...breConfigs];
    copy[idx][field] = value;
    setBreConfigs(copy);
  };

  const updateThirdParty = (checkpoint, provider, mode) => {
    setBreConfigs(prev =>
      prev.map(cp =>
        cp.checkpoint === checkpoint
          ? {
              ...cp,
              thirdParties: cp.thirdParties.map(tp =>
                tp.provider === provider ? { ...tp, mode } : tp
              )
            }
          : cp
      )
    );
  };

  const updateRule = (checkpoint, provider, ruleCode, mode) => {
    setBreConfigs(prev =>
      prev.map(cp =>
        cp.checkpoint === checkpoint
          ? {
              ...cp,
              thirdParties: cp.thirdParties.map(tp =>
                tp.provider === provider
                  ? {
                      ...tp,
                      rules: tp.rules.map(r =>
                        r.ruleCode === ruleCode ? { ...r, mode } : r
                      )
                    }
                  : tp
              )
            }
          : cp
      )
    );
  };

  /* =========================
     SAVE ALL (POST)
  ==========================*/
  const saveAllCheckpoints = async () => {
    try {
      const checkpointModes = {};
      const ruleModes = {};

      breConfigs.forEach(cp => {
        if (!cp.checkpoint) return;

        checkpointModes[cp.checkpoint] =
          cp.isActive ? cp.mode : "DISABLED";

        cp.thirdParties?.forEach(tp => {
          if (tp.mode !== "AUTO") return;

          if (!ruleModes[cp.checkpoint]) {
            ruleModes[cp.checkpoint] = {};
          }

          tp.rules?.forEach(rule => {
            ruleModes[cp.checkpoint][rule.ruleCode] = rule.mode;
          });
        });
      });

      await breAxios.post("/fintech/bre-config", {
        fintechId,
        checkpointModes,
        ruleModes
      });

      toast.success("BRE configuration saved");
      loadBreConfig();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  /* =========================
     UPDATE CHECKPOINT ONLY
  ==========================*/
  const updateCheckpointOnly = async (checkpoint, mode) => {
    await breAxios.put("/fintech/bre-config/checkpoint", {
      fintechId,
      checkpointModes: {
        [checkpoint]: mode
      }
    });
  };

  /* =========================
     UPDATE SINGLE RULE
  ==========================*/
  const updateSingleRule = async (checkpoint, ruleName, mode) => {
    await breAxios.put("/fintech/bre-config/rule", {
      fintechId,
      checkpointId: checkpoint,
      ruleName,
      mode
    });
  };

  /* =========================
     UPDATE MULTIPLE RULES
  ==========================*/
  const updateMultipleRules = async (checkpoint, rules) => {
    await breAxios.put("/fintech/bre-config/rules", {
      fintechId,
      checkpointId: checkpoint,
      rules
    });
  };

  // bre end 

  const handleAddPolicyField = (field) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: [...(prev[field] || []), { min_score: '', max_score: '', value: '' }],
    }));
  };
  const handleDeletePolicyField = (field, index) => {
    const updated = [...editFormData[field]];
    updated.splice(index, 1);
    setEditFormData((prev) => ({ ...prev, [field]: updated }));
  };

  const handleEditClick = (policyKey, policy) => {
    setEditingPolicyKey(policyKey);
    setEditFormData({
      bureau_type: policy.bureau_type,
      cut_off_min: policy.cut_off_min,
      rate_of_interest: policy.rate_of_interest.map(r => ({ ...r })),
      processing_fee: policy.processing_fee.map(p => ({ ...p })),
      foir: policy.foir.map(f => ({ ...f })),
      tenure_days: policy.tenure_days.map(t => ({ ...t })),
    });
  };

  const handleCancelEdit = () => {
    setEditingPolicyKey(null);
    setEditFormData({});
  };

  const handlePolicyUpdate = async (lsp, product, version) => {
    const [oldlsp, selectedproduct] = productVersion.split("-");
    const payload = {
      lsp: form.name,
      product: selectedproduct,
      version,
      details: editFormData,
    };

    try {
      const res = await axiosInstance.post(URL.PRICING_UPDATE, payload);

      if (res.status === 200 && res.data?.version) {
        toast.success(`Pricing JSON updated successfully (version: ${res.data.version})`);
        setEditingPolicyKey(null);
        handleProductClick(lsp, product); // reload updated data
      } else {
        toast.error(res.data?.message || "Update failed");
      }

    } catch (error) {
      console.error("Update API error:", error);
      toast.error("Failed to update policy.");
    }
  };



  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await axiosInstance.get(URL.LSP_STATE_LIST);
        setStateList(res.data);
      } catch (err) {
        console.error("Failed to load state list", err);
      }
    };

    fetchStates();
  }, []);





  useEffect(() => {
    axiosInstance
      .get(`${URL.LSP_LIST_FINTECHID}?id=${fintechId}`)
      .then((res) => {
        const data = res.data; // define data

        setForm((prev) => ({
          ...prev,
          ...data,
          webhookUrl: data.webhookUrl || "",
          foreclosure: data.foreclosure || prev.foreclosure,
        }));

        if (data?.name) {
          handleRowClick(data.name);
        }
      })
      .catch((err) => {
        setMessage("Failed to load data");
        console.error(err);
      });
  }, [fintechId]);



  const handleRowClick = async (lspName) => {
    if (expandedRow === lspName) return setExpandedRow(null);
    try {
      const res = await axiosInstance.get(`${URL.PRICING_LIST}?lsp=${lspName}`);
      const products = res?.data?.products || [];
      setLspProducts((prev) => ({ ...prev, [lspName]: products }));
      setExpandedRow(lspName);
    } catch {
      toast.error("Failed to fetch LSP products.");
    }
  };


  const handleProductClick = async (lspName, productName) => {
    setProductVersion(`${lspName}-${productName}`);
    const key = `${lspName}-${productName}`;
    const isExpanded = expandedProductVersions[key];
    setExpandedProductVersions((prev) => ({ ...prev, [key]: !isExpanded }));
    if (isExpanded) return;

    try {
      const res = await axiosInstance.get(`${URL.PRICING_LIST}?lsp=${lspName}&product=${productName}`);
      const policies = res?.data?.policies || [];
      setProductPolicies((prev) => ({ ...prev, [key]: policies }));
    } catch {
      toast.error("Failed to fetch policies.");
    }
  };



  const handleVersionClick = async (productKey, version) => {

    const key = `${productKey}-${version}`;
    const [lsp, product] = productKey.split("-");
    const isExpanded = expandedVersionDetails[key];
    setExpandedVersionDetails((prev) => ({ ...prev, [key]: !isExpanded }));
    if (isExpanded) return;

    try {
      const res = await axiosInstance.get(`${URL.PRICING_LIST}?lsp=${lsp}&product=${product}&version=${version}`);
      const details = res?.data?.details;
      if (!details) return toast.error("No version details found.");
      setProductPolicies((prev) => {
        const updated = [...(prev[productKey] || [])];
        const index = updated.findIndex(p => p.version === version);
        if (index !== -1) updated[index] = { ...details, version };
        else updated.push({ ...details, version });
        return { ...prev, [productKey]: updated };
      });
    } catch {
      toast.error("Version details fetch failed.");
    }
  };



  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : value;
    setForm((prev) => ({ ...prev, [name]: val }));
  };

  const handleForeclosureChange = (type, field, value) => {
    setForm((prev) => ({
      ...prev,
      foreclosure: {
        ...prev.foreclosure,
        [type]: {
          ...prev.foreclosure[type],
          [field]: parseInt(value),
        },
      },
    }));
  };



  // product slab  start 


  const [productSlabs, setProductSlabs] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [bulkSlabs, setBulkSlabs] = useState([
    {
      minAmount: "",
      maxAmount: "",
      tenureDays: "",
      dailyRoi: "",
      apr: "",
      isActive: true
    }
  ]);

  const loadProductSlabs = async () => {
    try {
      const res = await axiosInstance.get(
        `${URL.PRODUCT_SLAB}/soft/product?fintechId=${fintechId}`
      );
      setProductSlabs(res.data.data || []);
    } catch {
      toast.error("Failed to load product slabs");
    }
  };

  useEffect(() => {
    if (fintechId) loadProductSlabs();
  }, [fintechId]);




  const addBulkRow = () => {
    setBulkSlabs(prev => [
      ...prev,
      { minAmount: "", maxAmount: "", tenureDays: "", dailyRoi: "", apr: "", isActive: true }
    ]);
  };

  const updateBulkRow = (index, key, value) => {
    const copy = [...bulkSlabs];
    copy[index][key] = value;
    setBulkSlabs(copy);
  };

  const removeBulkRow = index => {
    const copy = [...bulkSlabs];
    copy.splice(index, 1);
    setBulkSlabs(copy);
  };

  const saveBulkSlabs = async () => {
    try {
      await axiosInstance.post(
        `${URL.PRODUCT_SLAB}/soft/productAdd?fintechId=${fintechId}`,
        bulkSlabs
      );
      toast.success("Slabs added in bulk");
      setShowAddForm(false);
      loadProductSlabs();
    } catch {
      toast.error("Bulk add failed");
    }
  };



  const updateSlab = async (slab) => {
    try {
      await axiosInstance.put(
        `${URL.PRODUCT_SLAB}/soft/productUpdate?fintechId=${fintechId}&slabId=${slab.id}`,
        slab
      );
      toast.success("Slab updated");
      loadProductSlabs();
    } catch {
      toast.error("Update failed");
    }
  };


// productSlabs end



  const handleSave = async () => {
    try {
      const res = await axiosInstance.post(`${URL.LSP_LIST_UPDATE}?id=${fintechId}`, form);
      const { success, message } = res.data;

      if (success) {
        toast.success(message || "Updated successfully");
        setEditMode(false);
      } else {
        toast.error(message || "Update failed");
      }

    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.message || "Update failed";
      toast.error(errorMessage);
      console.error("Update error:", err);
    }
  };


  const renderVersionDetails = (policy, productKey) => {
    const editing = editingPolicyKey === `${productKey}-${policy.version}`;

    const renderPolicyField = (label, field, keyLabel = 'value') => (
      <div className="mb-2">
        <strong>{label}:</strong>
        {(editing ? editFormData[field] : policy[field])?.map((item, idx) => (
          <Row key={idx} className="mb-1 align-items-center">
            <Col md={2}><Input type="number" value={item.min_score} onChange={(e) => editing && handlePolicyEditChange(field, idx, 'min_score', e.target.value)} /></Col>
            <Col md={2}><Input type="number" value={item.max_score} onChange={(e) => editing && handlePolicyEditChange(field, idx, 'max_score', e.target.value)} /></Col>
            <Col md={2}><Input type="number" value={item[keyLabel] || item.rate || item.fee || item.foir || item.tenure} onChange={(e) => editing && handlePolicyEditChange(field, idx, keyLabel, e.target.value)} /></Col>
            {editing && (
              <Col md={1} className="text-danger">
                <FaTrash style={{ cursor: "pointer" }} onClick={() => handleDeletePolicyField(field, idx)} />
              </Col>
            )}
          </Row>
        ))}
        {editing && (
          <Button size="sm" color="secondary" onClick={() => handleAddPolicyField(field, ['min_score', 'max_score', keyLabel])} className="mt-1">
            <FaPlus /> Add Score
          </Button>
        )}
      </div>
    );

    return (
      <div className="mt-2 p-3 border rounded bg-light">
        <Row>
          <Col><strong>Bureau Type:</strong> {editing ? <Input value={editFormData.bureau_type} onChange={(e) => setEditFormData(prev => ({ ...prev, bureau_type: e.target.value }))} /> : policy.bureau_type}</Col>
          <Col><strong>Cut Off Min:</strong> {editing ? <Input value={editFormData.cut_off_min} onChange={(e) => setEditFormData(prev => ({ ...prev, cut_off_min: e.target.value }))} /> : policy.cut_off_min}</Col>
        </Row>

        {renderPolicyField('Interest Rate', 'rate_of_interest', 'rate')}
        {renderPolicyField('Processing Fee', 'processing_fee', 'fee')}
        {renderPolicyField('FOIR', 'foir', 'foir')}
        {renderPolicyField('Tenure (Days)', 'tenure_days', 'tenure')}

        <div className="mt-3">
          {editing ? (
            <>
              <Button
                color="success"
                size="sm"
                onClick={() => {
                  // const [lsp, product] = productKey ? productKey.split("-") : ["", ""];


                  const parts = productKey ? productKey.split("-") : [];
                  const lsp = parts[0] || "";
                  const product = parts[1] || "";

                  handlePolicyUpdate(lsp, product, policy.version);
                }}
              >
                <FaSave /> Save
              </Button>



              <Button size="sm" color="danger" onClick={handleCancelEdit}><FaTimes /> Cancel</Button>
            </>
          ) : (
            <Button size="sm" color="primary" onClick={() => handleEditClick(`${productKey}-${policy.version}`, policy)}>Edit</Button>
          )}
        </div>
      </div>
    );
  };




  return (
    <>




      {/* Gradient Header */}
      <div className="header bg-gradient-info pb-6 pt-4 pt-md-7">
        <Container fluid>
          <Row>
            <Col>
              <h1 className="text-white">Update LSP Details</h1>
            </Col>
          </Row>
        </Container>
      </div>

      <Container fluid className="mt--0">
        <Row>
          <Col>
            <Card className="shadow p-4">
              <CardBody>
                <Form onSubmit={handleSave}>
                  <Row form>
                    {[
                      ["name", "Name"],
                      ["userName", "UserName"],
                      ["password", "Password"],
                      ["emailId", "Email", "email"],
                      ["phoneNumber", "Phone"],
                      ["webhookUrl", "Webhook URL"], // ✅ Added this line
                      ["gstNumber", "GST Number"],
                      ["cinNumber", "CIN Number"],
                      ["companyPanNumber", "Company PAN"],
                      ["companyAddress", "Company Address"],
                      ["state", "State"],
                      ["spocName", "SPOC Name"],
                      ["spocPhoneNumber", "SPOC Phone"],
                      ["spocEmail", "SPOC Email"],
                      ["grievanceOfficerName", "Grievance Officer Name"],
                      ["grievanceOfficerPhone", "Grievance Officer Phone"],
                      ["grievanceOfficerEmail", "Grievance Officer Email"],
                      ["version", "Version"],



                    ].map(([name, label, type = "text"]) => (
                      <Col md={4} key={name}>
                        <FormGroup>
                          <Label>{label}</Label>
                          {name === "state" ? (
                            <Input
                              type="select"
                              name="state"
                              value={form.state}
                              onChange={handleChange}
                            >
                              <option value="">Select State</option>
                              {stateList.map((stateName, index) => (
                                <option key={index} value={stateName}>
                                  {stateName}
                                </option>
                              ))}
                            </Input>
                          ) : (
                            <Input
                              type={type}
                              name={name}
                              value={form[name] || ""}
                              onChange={handleChange}
                            />
                          )}
                        </FormGroup>
                      </Col>
                    ))}

                    <Col md={4}>
                      <FormGroup>
                        <Label>Bureau Type</Label>
                        <Input
                          type="select"
                          name="bureauType"
                          value={form.bureauType}
                          onChange={handleChange}
                        >
                          <option value={1}>CIBIL</option>
                          <option value={2}>Equifax</option>
                          <option value={3}>Experian</option>
                          <option value={4}>CRIF</option>
                        </Input>
                      </FormGroup>
                    </Col>

                    <Col md={4} className="d-flex align-items-center">
                      <FormGroup check>
                        <Label check>
                          <Input
                            type="checkbox"
                            name="isActive"
                            checked={form.isActive}
                            onChange={handleChange}
                          />{" "}
                          Active
                        </Label>
                      </FormGroup>
                    </Col>
                  </Row>

                  <h2 className="mt-4">Foreclosure </h2>
                  {["daily", "weekly", "monthly", "bullet"].map((type) => (
                    <Row key={type} className="mb-3">
                      <Col md={12}>
                        <strong>{type.toUpperCase()}</strong>
                      </Col>
                      {["paid_emi", "foreclosure_fees", "force_foreclosure_fees"].map((field) => (
                        <Col md={4} key={field}>
                          <FormGroup>
                            <Label>{field.replace(/_/g, " ")}</Label>
                            <Input
                              type="number"
                              value={form.foreclosure[type]?.[field] || 0}
                              onChange={(e) =>
                                handleForeclosureChange(type, field, e.target.value)

                              }
                            />
                          </FormGroup>
                        </Col>
                      ))}
                    </Row>
                  ))}

                  {!editMode ? (

                    <Button color="primary" type="button" onClick={() => setEditMode(true)}>
                      Update Fintech
                    </Button>
                  ) : (
                    <>
                      <Button color="success" type="button" onClick={handleSave}>
                        Save
                      </Button>{" "}
                      <Button color="secondary" type="button" onClick={() => setEditMode(false)}>
                        Cancel
                      </Button>
                    </>
                  )}


                  {expandedRow === form.name && lspProducts[form.name]?.length > 0 && (
                    <Card className="mt-4 shadow">
                      <CardBody>
                        <h3>Pricing for {form.name}</h3>
                        <Table className="table-bordered mb-0">
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lspProducts[form.name].map((product) => {
                              const productKey = `${form.name}-${product}`;
                              const isExpanded = expandedProductVersions[productKey];
                              return (
                                <React.Fragment key={productKey}>
                                  <tr>
                                    <td><strong>{product}</strong></td>
                                    <td>
                                      <Button
                                        size="sm"
                                        color="primary"
                                        onClick={() => handleProductClick(form.name, product)}
                                      >
                                        {isExpanded ? "Hide Versions" : "View Versions"}
                                      </Button>
                                    </td>
                                  </tr>
                                  {isExpanded && (
                                    <tr>
                                      <td colSpan="2">

                                        {productPolicies[productKey]?.length > 0 ? (
                                          <ul className="mb-0">
                                            {productPolicies[productKey].map((policy) => (
                                              <li key={`${productKey}-${policy.version}`}>
                                                <strong>Version:</strong>{" "}
                                                <span
                                                  onClick={() => handleVersionClick(productKey, policy.version)}

                                                  style={{ cursor: "pointer", color: "blue" }}
                                                >
                                                  {policy.version}
                                                </span>
                                                {expandedVersionDetails[`${productKey}-${policy.version}`] &&
                                                  renderVersionDetails(policy)}
                                              </li>
                                            ))}
                                          </ul>
                                        ) : (
                                          <p className="mb-0">No versions available</p>
                                        )}

                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </Table>
                      </CardBody>
                    </Card>
                  )}
                </Form>
              </CardBody>

            


<Card className="mt-4 shadow">
      <CardBody>
        <h3>BRE Checkpoint Configuration</h3>

        <Table bordered>
          <thead>
            <tr>
              <th>Checkpoint</th>
              <th>Mode</th>
              <th>Active</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {breConfigs.map((row, idx) => (
              <React.Fragment key={idx}>
                <tr>
                  <td>
                    <Input
                      type="number"
                      value={row.checkpoint}
                      onChange={e =>
                        updateCheckpoint(idx, "checkpoint", e.target.value)
                      }
                    />
                  </td>

                  <td>
                    <Input
                      type="select"
                      value={row.mode}
                      onChange={e =>
                        updateCheckpoint(idx, "mode", e.target.value)
                      }
                    >
                      {BRE_MODES.map(m => (
                        <option key={m}>{m}</option>
                      ))}
                    </Input>
                  </td>

                  <td className="text-center">
                    <Input
                      type="checkbox"
                      checked={row.isActive}
                      onChange={e =>
                        updateCheckpoint(idx, "isActive", e.target.checked)
                      }
                    />
                  </td>

                  <td>
                    <Button
                      size="sm"
                      color="info"
                      onClick={() =>
                        setExpandedCheckpoint(
                          expandedCheckpoint === row.checkpoint
                            ? null
                            : row.checkpoint
                        )
                      }
                    >
                      Config
                    </Button>{" "}
                    <Button
                      size="sm"
                      color="danger"
                      onClick={() => removeCheckpoint(idx)}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>

                {expandedCheckpoint === row.checkpoint && (
                  <tr>
                    <td colSpan="4">
                      {row.thirdParties.map(tp => (
                        <div key={tp.provider} className="ml-4 mt-3">
                          <Row>
                            <Col md={3}><b>{tp.provider}</b></Col>
                            <Col md={3}>
                              <Input
                                type="select"
                                value={tp.mode}
                                onChange={e =>
                                  updateThirdParty(
                                    row.checkpoint,
                                    tp.provider,
                                    e.target.value
                                  )
                                }
                              >
                                {RULE_MODES.map(m => (
                                  <option key={m}>{m}</option>
                                ))}
                              </Input>
                            </Col>
                          </Row>

                          {tp.rules.map(rule => (
                            <Row key={rule.ruleCode} className="ml-5 mt-2">
                              <Col md={3}>{rule.ruleName}</Col>
                              <Col md={3}>
                                <Input
                                  type="select"
                                  value={rule.mode}
                                  onChange={e =>
                                    updateRule(
                                      row.checkpoint,
                                      tp.provider,
                                      rule.ruleCode,
                                      e.target.value
                                    )
                                  }
                                >
                                  {RULE_MODES.map(m => (
                                    <option key={m}>{m}</option>
                                  ))}
                                </Input>
                              </Col>
                            </Row>
                          ))}
                        </div>
                      ))}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </Table>

        <Button color="secondary" onClick={addCheckpoint}>
          <FaPlus /> Add Checkpoint
        </Button>

        <Button color="success" className="ml-2" onClick={saveAllCheckpoints}>
          <FaSave /> Save All
        </Button>
      </CardBody>
    </Card>
              {/* product slab start  */}


              <Card className="mt-4 shadow">
                <CardBody>
                  <h3>Product Slabs</h3>

                  <Table bordered>
                    <thead>
                      <tr>
                        <th>Min</th>
                        <th>Max</th>
                        <th>Tenure</th>
                        <th>Daily ROI</th>
                        <th>APR</th>
                        <th>Active</th>
                        <th>Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {productSlabs.map((slab, idx) => (
                        <tr key={idx}>
                          {["minAmount", "maxAmount", "tenureDays", "dailyRoi", "apr"].map(k => (
                            <td key={k}>
                              <Input
                                type="number"
                                value={slab[k]}
                                onChange={e => {
                                  const copy = [...productSlabs];
                                  copy[idx][k] = e.target.value;
                                  setProductSlabs(copy);
                                }}
                              />
                            </td>
                          ))}

                          <td className="text-center">
                            <Input
                              type="checkbox"
                              checked={slab.isActive}
                              onChange={e => {
                                const copy = [...productSlabs];
                                copy[idx].isActive = e.target.checked;
                                setProductSlabs(copy);
                              }}
                            />
                          </td>

                          <td>
                            <Button size="sm" color="success" onClick={() => updateSlab(slab)}>
                              <FaSave />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  <Button color="primary" className="mt-3" onClick={() => setShowAddForm(!showAddForm)}>
                    <FaPlus /> Add Bulk Slabs
                  </Button>

                  {showAddForm && (
                    <>
                      <hr />
                      <h4>Bulk Add Slabs</h4>

                      {bulkSlabs.map((row, idx) => (
                        <Row key={idx} className="mb-2">
                          {["minAmount", "maxAmount", "tenureDays", "dailyRoi", "apr"].map(k => (
                            <Col md={2} key={k}>
                              <Input
                                placeholder={k}
                                type="number"
                                value={row[k]}
                                onChange={e => updateBulkRow(idx, k, e.target.value)}
                              />
                            </Col>
                          ))}
                          <Col md={1}>
                            <Input
                              type="checkbox"
                              checked={row.isActive}
                              onChange={e => updateBulkRow(idx, "isActive", e.target.checked)}
                            />
                          </Col>
                          <Col md={1}>
                            <FaTrash
                              style={{ cursor: "pointer", color: "red" }}
                              onClick={() => removeBulkRow(idx)}
                            />
                          </Col>
                        </Row>
                      ))}

                      <Button size="sm" color="secondary" onClick={addBulkRow}>
                        <FaPlus /> Add Row
                      </Button>{" "}
                      <Button size="sm" color="success" onClick={saveBulkSlabs}>
                        <FaSave /> Save Bulk
                      </Button>
                    </>
                  )}
                </CardBody>
              </Card>


            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default FintechUpdatePage;